import LayerGroup from "ol/layer/Group";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";

import { getBasicObjectTypeLayer } from "../layers";
import { Geometry } from "ol/geom";
import { buildStyle } from "./buildStyle";
import { configureWmsLayerFilter } from "./configureLayerFilter";
import { store } from "../../../../stateManagement/store";
import { getRankingStyleForLayerName } from "./rankingStyles";

function buildGeoJsonLayer(cfg: any) {
    const source = new VectorSource<Geometry>({
        url: cfg.url,
        format: new GeoJSON({ dataProjection: "EPSG:4326" })
    });

    if (cfg.data) {
        source.addFeatures(
            new GeoJSON().readFeatures(cfg.data, {
                dataProjection: "EPSG:4326",
                featureProjection: "EPSG:3857"
            })
        );
    }

    const layer = new VectorLayer<VectorSource<Geometry>>({
        zIndex: 900,
        source
    });

    if (cfg.style) {
        layer.setStyle(buildStyle(cfg.style));
    }
    return layer;
}

function isLayerVisible(
    cfg: any,
    visFilterName: string | undefined,
    visValues: Record<string, unknown> | undefined,
    visIgnored: boolean | undefined
) {
    if (!visFilterName || !cfg?.id) return true;
    // Если для слоя указаны applicableFilters и среди них нет данного фильтра видимости —
    // фильтр к слою не применяется → слой видим.
    const applicableFilters: string[] | undefined = cfg?.applicableFilters;
    if (Array.isArray(applicableFilters) && !applicableFilters.includes(visFilterName)) return true;
    return Boolean((visValues as any)?.[cfg.id] || visIgnored);
}

export function buildAdditionalMapFilteredLayer(
    menuItem: any,
    appliedFilters: any = {},
    mapsFiltersExpressions: any = {},
    currentConfig?: any,
    wmsEnv?: string
) {
    if (!menuItem) return undefined;

    const { wmsServer, wmsVersion, workspace } = currentConfig ?? {};
    const { mapLayer } = menuItem;
    if (!mapLayer) return undefined;
    const mapRankingIsEnabled = store.getState().contentHeader.mapRankingIsEnabled;

    if (!Array.isArray(mapLayer)) {
        const { sourceType } = mapLayer;
        if (!sourceType) {
            console.error("mapLayer: обязательно укажите sourceType", mapLayer);
            return undefined;
        }

        switch (sourceType.toUpperCase()) {
            case "WMS":
                // Применяем ranking стиль только если есть wmsEnv
                const singleLayerStyle = (mapRankingIsEnabled && wmsEnv)
                    ? (getRankingStyleForLayerName(mapLayer.name, mapLayer) ?? mapLayer.style)
                    : mapLayer.style;
                return getBasicObjectTypeLayer(
                    [mapLayer.name],
                    wmsServer,
                    wmsVersion,
                    workspace,
                    "INCLUDE",
                    singleLayerStyle,
                    wmsEnv
                );

            case "GEOJSON":
                return buildGeoJsonLayer(mapLayer);

            default:
                console.error("Неизвестный sourceType:", sourceType, mapLayer);
                return undefined;
        }
    }

    const wmsNames: string[] = [];
    const wmsFilters: string[] = [];
    const wmsStyles: (string | null)[] = [];
    const vectorLayers: VectorLayer[] = [];

    const [visName, visFilter]: any =
        Object.entries(appliedFilters).find(([, f]: any) => f && f.type === "layer") ?? [];

    const visValues = visFilter?.appliedValues;
    const visIgnored = visFilter?.isIgnored;

    
    mapLayer.forEach((cfg: any) => {
        if (!cfg.sourceType) {
            console.error("mapLayer: не указан sourceType", cfg);
            return;
        }
        
        if (!isLayerVisible(cfg, visName, visValues, visIgnored)) return;

        switch (cfg.sourceType.toUpperCase()) {
            case "WMS": {
                const dynFilter = configureWmsLayerFilter(
                    { cfg, menuItem, mapsFiltersExpressions, appliedFilters },
                    cfg.staticFilter
                );

                wmsNames.push(cfg.name);
                wmsFilters.push(dynFilter || "INCLUDE");
                // Применяем ranking стиль только если есть wmsEnv
                const rankedStyle = (mapRankingIsEnabled && wmsEnv)
                    ? (getRankingStyleForLayerName(cfg.name, cfg) ?? cfg.style)
                    : cfg.style;
                wmsStyles.push(rankedStyle ?? null);
                break;
            }

            case "GEOJSON":
                vectorLayers.push(buildGeoJsonLayer(cfg));
                break;

            default:
                console.error("Неизвестный sourceType:", cfg.sourceType, cfg);
        }
    });

    if (!wmsNames.length && !vectorLayers.length) return undefined;

    const layers: any[] = [];

    if (wmsNames.length) {
        layers.push(
            getBasicObjectTypeLayer(
                wmsNames,
                wmsServer,
                wmsVersion,
                workspace,
                wmsFilters.join(";"),
                wmsStyles.some(s => s) ? wmsStyles.join(",") : undefined,
                wmsEnv
            )
        );
    }
    if (vectorLayers.length) layers.push(...vectorLayers);

    return layers.length === 1 ? layers[0] : new LayerGroup({ layers });
}